import {
  Typography,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  TextField,
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { difference, mean } from 'lodash'
import React, { useState } from 'react'
import { ChevronDown, Trash } from 'react-feather'
import { useTranslation } from 'react-i18next'
import { EnergyType } from '../../calculators/EnergyFlow'
import {
  PricingModelFragment,
  ProjectFragment,
  ProjectVersionFragment,
  PurchasedEnergyType,
  UpdatePricingModelInput,
} from '../../generated/graphql'
import { useOnDeletePricingModel } from '../../pricing-model/use-on-delete-pricing-model'
import { usePricingModels } from '../../pricing-model/use-pricing-models'
import { useProjectVersions } from '../../project-versions/use-project-versions'
import { sortByProperty } from '../../util/array-utility'
import {
  energyPriceUnit,
  formatEnergyPrice,
  formatPercentWithPrecision,
} from '../../util/formatters'
import {
  mapFormValuesToInput,
  mapPricingModelToValues,
  PricingModelDialog,
  PricingModelFormValues,
  PricingModelStepType,
} from './PricingModelDialog'
import { PricingModelTable } from './PricingModelTable'
import { useSidebarStyles } from './use-sidebar-styles'

type Props = {
  pricingModel: PricingModelFragment
  project: ProjectFragment
}

type BasicInfoFormatters = {
  buildingYear: (value: number) => string
  inspectionLengthInYears: (value: number) => string
  yearlyDiscountRate: (value: number) => string
  yearlyMaintenanceIncreaseRate: (value: number) => string
}

export const basicInfoKeys = [
  'buildingYear',
  'inspectionLengthInYears',
  'yearlyDiscountRate',
  'yearlyMaintenanceIncreaseRate',
] as const
export const basicInfoFormatters: BasicInfoFormatters = {
  buildingYear: (value: number) => String(value),
  inspectionLengthInYears: (value: number) => String(value),
  yearlyDiscountRate: (value: number) =>
    formatPercentWithPrecision(value, { omitUnit: true }),
  yearlyMaintenanceIncreaseRate: (value: number) =>
    formatPercentWithPrecision(value, { omitUnit: true }),
}

export const formatPriceByEnergyType = (
  energyType: PurchasedEnergyType,
  arrayName: 'prices' | 'sellingPrices',
  pricingModel: PricingModelFragment,
): string => {
  const priceByEnergyType = pricingModel[arrayName].find(
    (price) => price.energyType === energyType,
  )
  const value = priceByEnergyType ? mean(priceByEnergyType.monthlyValues) : 0
  return formatEnergyPrice(value, { omitUnit: true })
}

export const formatPriceIncreaseRateByEnergyType = (
  energyType: PurchasedEnergyType,
  pricingModel: PricingModelFragment,
): string => {
  const priceIncreaseRateByEnergyType = pricingModel.priceIncreaseRates.find(
    (price) => price.energyType === energyType,
  )
  const value = priceIncreaseRateByEnergyType?.yearlyValue ?? 0
  return formatPercentWithPrecision(value, { omitUnit: true })
}

export function PricingModelAccordion({
  pricingModel,
  project,
}: Props): React.ReactElement {
  const classes = useSidebarStyles()
  const { t } = useTranslation()
  const [activeEditStep, setActiveEditStep] =
    useState<PricingModelStepType | null>(null)
  const { updatePricingModel } = usePricingModels()
  const { onDeletePricingModel } = useOnDeletePricingModel()
  const { updateProjectVersion } = useProjectVersions()

  const selectedVersions = project.versions.filter(
    (version) => version.pricingModel?.id === pricingModel.id,
  )

  const onSelectedVersionsChange = async (
    versions: ProjectVersionFragment[],
  ) => {
    const oldVersionIds = selectedVersions.map((version) => version.id)
    const newVersionIds = versions.map((version) => version.id)

    const addedVersionIds = difference(newVersionIds, oldVersionIds)
    const addPromises = addedVersionIds.map((addedVersionId) =>
      updateProjectVersion(addedVersionId, { pricingModelId: pricingModel.id }),
    )

    const removedVersionIds = difference(oldVersionIds, newVersionIds)
    const removePromises = removedVersionIds.map((removedVersionId) =>
      updateProjectVersion(removedVersionId, { pricingModelId: null }),
    )

    await Promise.all([...addPromises, ...removePromises])
  }

  const onEdit = async (values: PricingModelFormValues) => {
    const input = mapFormValuesToInput<UpdatePricingModelInput>(values)
    await updatePricingModel(pricingModel.id, input)
    setActiveEditStep(null)
  }

  const onDelete = () => {
    onDeletePricingModel(pricingModel, project)
  }

  return (
    <>
      <Accordion square>
        <AccordionSummary
          expandIcon={<ChevronDown />}
          className={classes.accordionContentContainer}
        >
          <Typography variant="h6">{pricingModel.name}</Typography>
        </AccordionSummary>

        <AccordionDetails className={classes.accordionContentContainer}>
          <Box className={classes.accordionContent}>
            <Autocomplete
              id="pricing-model-selected-version-ids"
              options={sortByProperty(project.versions, 'name')}
              getOptionLabel={(version) => version.name}
              getOptionSelected={(option, value) => option.id === value.id}
              value={selectedVersions}
              onChange={(_, value) => onSelectedVersionsChange(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('pricingModels.usedInVersions')}
                  placeholder={t('pricingModels.usedInVersionsPlaceholder')}
                  className={classes.autoCompleteField}
                />
              )}
              limitTags={1}
              multiple
            />

            <PricingModelTable
              header={t('pricingModels.basicInfo')}
              rowsData={basicInfoKeys.map((key) => ({
                name: t(`pricingModels.${key}`),
                value: basicInfoFormatters[key](pricingModel[key]),
              }))}
              onStartEdit={() =>
                setActiveEditStep(PricingModelStepType.BasicInfo)
              }
            />

            <PricingModelTable
              header={`${t('pricingModels.energyPrices')}, ${energyPriceUnit}`}
              rowsData={Object.values(PurchasedEnergyType).map(
                (energyType) => ({
                  name: t(`purchasedEnergyType.${energyType}`),
                  value: formatPriceByEnergyType(
                    energyType,
                    'prices',
                    pricingModel,
                  ),
                  tooltip: t('pricingModels.energyPriceTotalValueTooltip'),
                }),
              )}
              onStartEdit={() =>
                setActiveEditStep(PricingModelStepType.EnergyPrice)
              }
            />

            <PricingModelTable
              header={`${t(
                'pricingModels.energySellingPrices',
              )}, ${energyPriceUnit}`}
              rowsData={[PurchasedEnergyType.Electricity].map((energyType) => ({
                name: t(`energyType.${EnergyType.Electricity}`),
                value: formatPriceByEnergyType(
                  energyType,
                  'sellingPrices',
                  pricingModel,
                ),
                tooltip: t('pricingModels.energyPriceTotalValueTooltip'),
              }))}
              onStartEdit={() =>
                setActiveEditStep(PricingModelStepType.EnergySellingPrice)
              }
            />

            <PricingModelTable
              header={`${t('pricingModels.energyPriceIncreaseRate')}, %`}
              rowsData={Object.values(PurchasedEnergyType).map(
                (energyType) => ({
                  name: t(`purchasedEnergyType.${energyType}`),
                  value: formatPriceIncreaseRateByEnergyType(
                    energyType,
                    pricingModel,
                  ),
                }),
              )}
              onStartEdit={() =>
                setActiveEditStep(PricingModelStepType.EnergyPriceIncreaseRate)
              }
            />

            <Button variant="contained" onClick={onDelete} fullWidth>
              <Box className={classes.buttonContent}>
                <Trash />
                <Typography variant="body1">
                  {t('pricingModels.remove')}
                </Typography>
              </Box>
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>

      <PricingModelDialog
        isOpen={activeEditStep !== null}
        defaultValues={mapPricingModelToValues(pricingModel)}
        stepType={activeEditStep}
        onSubmit={onEdit}
        onClose={() => setActiveEditStep(null)}
      />
    </>
  )
}
